import React, { useEffect, useRef, useState } from 'react';
import { queryCache } from 'react-query';
import { QKeys } from 'src/api/types';
import { GTCInlineWrapper } from 'src/components/GenericTable';
import { useUpsertControlMutation } from 'src/graphql/_generated';

import { useAuth } from 'src/state/auth';
import MaterialDatePicker from 'src/components/form/MaterialDatePicker';
import styled from 'styled-components';

const Wrapper = styled.div<{ $error?: boolean }>`
  width: 100%;
  height: 100%;

  .MuiFormControl-root {
    width: 100%;
    height: 100%;
    margin: 0;
  }
  .MuiInputBase-root {
    width: 100%;
    height: 100%;
    font: inherit;
    background: transparent !important;

    &::before,
    &::after {
      display: none;
    }
  }
  .MuiOutlinedInput-notchedOutline {
    border: ${({ $error, theme }) => ($error ? `1px solid ${theme.colors.error}` : 'none')} !important;
  }
  .MuiInputBase-input {
    height: 100%;
    padding: 0 14px;
    font: inherit;
    cursor: pointer;
    box-sizing: border-box;
  }
  .MuiIconButton-root {
    padding: 4px;
    margin-right: 4px;
  }
`;

type DateField = 'expectedActiveDate' | 'actualActiveDate' | 'reviewDate';

interface DateCellProps {
  controlId: string;
  field: DateField;
  value: string | null | undefined;
}

export const DateCell: React.FC<DateCellProps> = ({ controlId, field, value }) => {
  const [upsertControl, { loading }] = useUpsertControlMutation();
  const { workspaceId } = useAuth();
  const [draft, setDraft] = useState<string | null>(value ?? null);
  const draftRef = useRef(draft);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(value ?? null);
    draftRef.current = value ?? null;
  }, [value]);

  const save = (date: string | null) => {
    if (error) return;
    const newValue = date || null;
    const oldValue = value || null;
    if (newValue === oldValue) return;
    upsertControl({
      variables: {
        controlData: { id: controlId, [field]: newValue },
        workspaceId,
      },
      update: (cache) => {
        cache.modify({
          id: cache.identify({ __typename: 'Control', id: controlId }),
          fields: {
            [field]: () => newValue,
          },
        });
      },
      onCompleted: () => {
        queryCache.invalidateQueries([QKeys.Audit]);
      },
    });
  };

  return (
    <GTCInlineWrapper>
      <Wrapper $error={!!error}>
        <MaterialDatePicker
          value={draft}
          onChange={(date) => {
            setDraft(date);
            draftRef.current = date;
          }}
          onAccept={(date) => {
            const iso = date ? date.toISOString() : null;
            save(iso);
          }}
          onClose={() => save(draftRef.current)}
          onError={(reason) => setError(reason)}
          disabled={loading}
          inputProps={{
            onBlur: () => save(draftRef.current),
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            },
          }}
        />
      </Wrapper>
    </GTCInlineWrapper>
  );
};
